/**
 * useInlineConversation — MS59 conversation engine for the landing
 * inline concierge surface.
 *
 * Extracts the chat-turn run-loop that's been living inside
 * `src/pages/Chat.tsx` into a reusable hook so the landing hero can
 * host the same conversation experience without redirecting the user
 * to /chat. Returns one cohesive bag of state + actions an embedding
 * component (InlineConcierge) can render directly.
 *
 * Why this exists now (the original-intent backstory):
 *   Sprint 1.4's `SmartIntentBar` was designed around the Krishna
 *   principle — "chat-vs-browse mode-choice collapsed into one input;
 *   the system disambiguates instead of the user." The current
 *   `navigate()` to /chat or /browse violates that intent — we just
 *   defer the mode-choice into a page transition. Inlining is the
 *   truer implementation of Sprint 1.4. The comment in SmartIntentBar
 *   even flags it: "// Routing (pre-Phase 2 placeholder)" — Phase 2
 *   was always meant to inline. This hook is the first half of Phase 2.
 *
 * Why a hook, not just lift Chat.tsx wholesale:
 *   Chat.tsx couples the engine to /chat-specific concerns: URL params
 *   (sessionId, sku_id, inspired_by, celebration_id, recipient,
 *   occasion), session restore from existing sessions, anchor-SKU
 *   hydration. The landing surface needs none of that — it's always
 *   a fresh session that starts from a brief the user just typed in
 *   the SmartIntentBar. The hook keeps the engine; the caller picks
 *   which bootstrap path it wants.
 *
 * What's NOT in scope here (deferred):
 *   - The 60s consolidation refactor: porting Chat.tsx itself onto
 *     this hook so both surfaces share one engine. Tracking as
 *     MS59.6. Doing it in MS59 risks regressing /chat; the duplication
 *     is acceptable for this PR.
 *   - Session restore (URL `?sessionId=`). The landing flow never
 *     restores — it always starts cold. /chat's restore stays in
 *     Chat.tsx until the consolidation.
 *   - Anchor SKU + inspired_by. Same reason: landing-flow-only here.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getOrCreateGuestSessionId } from "@/lib/guestSession";
import { captureInteraction } from "@/lib/captureInteraction";
import type { InteractionSurface } from "@/lib/captureInteraction";
import { track, EVENTS } from "@/lib/analytics";
import type { SkuCardData } from "@/components/chat/SkuCard";
import { CHAT_STREAMING_ENABLED } from "@/lib/chatStreaming";
import {
  consumeChatStream,
  ChatStreamTransportError,
  type BufferedTurnData,
} from "@/lib/chatStream";
import {
  applyDashboardClientActions,
  parseDashboardClientActions,
  type DashboardClientActionHandlers,
} from "@/lib/dashboardClientActions";
import { type TeamClientActionHandlers } from "@/lib/teamClientActions";
import { parseThreadWidgets, type ThreadWidget, type WidgetActionRequest } from "@/lib/threadWidgets";

/**
 * MS591/AUDIT-024: which UI surface a chat turn was served from, used as
 * the `surface` property on the `chat_turn_latency` PostHog event so
 * client-perceived latency can be broken down per surface.
 *
 *   - "chat"            → the full-page /chat surface (Chat.tsx)
 *   - "dashboard-drawer"→ the authed DashboardConciergeDrawer
 *   - "floating"        → the ask-anywhere FloatingConcierge bubble
 *   - "inline"          → the landing-hero InlineConcierge (and any other
 *                         inline embed) — the default for surfaces that
 *                         don't pass a more specific value
 *
 * Distinct from `InteractionSurface` (the interaction_captures taxonomy):
 * latency is BEHAVIOUR telemetry → PostHog, per the captureInteraction
 * module header's PostHog/interaction_captures split.
 */
export type LatencySurface = "chat" | "dashboard-drawer" | "floating" | "inline";

// ─── Types (mirror Chat.tsx so a future consolidation is a rename) ──

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  text: string;
  cards?: SkuCardData[];
  threadWidgets?: ThreadWidget[];
  animate?: boolean;
}

export type ChatStatus =
  | "ready"
  | "thinking"
  // MS598: the assistant turn is streaming in token-by-token. Reachable
  // ONLY on the flag-gated streaming path (CHAT_STREAMING_ENABLED, default
  // OFF) and ONLY after the first `delta` lands — it's the signal that the
  // pre-loader should have handed off to the growing bubble. The buffered
  // path never sets it, so with the flag off the union value is inert and
  // every surface stays byte-identical to pre-MS598 ("thinking" → done).
  // Finalizes back to "completed"/"ready" via the SAME transitions the
  // buffered path uses (see the done-side-effect block in submit).
  | "streaming"
  | "completed"
  | "circuit_broken"
  | "error";

export interface CostSnapshot {
  total_cost_usd_cents: number;
  near_circuit_breaker: boolean;
}

/** Brief shape the SmartIntentBar verdict shaping passes down. All
 *  fields optional — a cold "send Sarah a birthday gift" brief
 *  carries raw_text + recipient_name; a vendor_category-anchored
 *  brief might carry just raw_text + vendor_category. The
 *  chat-concierge edge function tolerates any subset. */
export interface InlineConversationBrief {
  raw_text?: string;
  recipient_name?: string;
  recipient_id?: string;
  occasion?: string;
  life_moment?: string;
  vendor_category?: string;
  budget_aud?: number;
  /** Linked celebration when opened from dashboard / floating widget. */
  celebration_id?: string;
  delivery_date?: string;
}

interface UseInlineConversationOptions {
  /** The brief the user submitted in SmartIntentBar. Passed to the
   *  edge function on the first turn so the concierge has full
   *  context (raw text + any slots the classifier extracted). */
  initialBrief?: InlineConversationBrief;
  /** When true, auto-submit `initialBrief.raw_text` as the user's
   *  first message on mount. The landing flow always wants this
   *  (the brief IS the first message). Default true. */
  autoSendFirstMessage?: boolean;
  /** Surface label used in interaction_captures. Lets us split
   *  landing-inline vs /chat-page metrics later. Default "landing". */
  captureSurface?: InteractionSurface;
  /** MS591/AUDIT-024: surface label for the `chat_turn_latency` PostHog
   *  event. When omitted, it's derived from `captureSurface` ("chat" →
   *  "chat", anything else → "inline"). Callers that wrap the shared
   *  InlineConcierge (FloatingConcierge, DashboardConciergeDrawer) can
   *  pass an explicit value here to attribute their turns precisely
   *  without changing the interaction_captures surface. */
  latencySurface?: LatencySurface;
  /** MS570: URL-driven session restore (the /chat page reads
   *  `?sessionId=` from the URL and passes it here). When set, the
   *  restore effect bypasses the sessionStorage `lembra:hero_session_id`
   *  path and uses this id directly. Implicitly suppresses the
   *  autoSendFirstMessage path — restoring a prior conversation should
   *  never re-fire the original brief. */
  restoreFromSessionId?: string | null;
  /** MS570: when restoring (either via `restoreFromSessionId` or the
   *  sessionStorage fallback), additionally fetch `chat_sessions.initial_brief`
   *  alongside the messages. The fetched brief is exposed via the
   *  `restoredInitialBrief` return field so the calling page can
   *  rehydrate header context (recipient name + occasion) without
   *  owning its own chat_sessions query. Default false (the landing
   *  hero / FloatingConcierge don't render a recipient header so they
   *  don't need this). */
  includeInitialBriefRestore?: boolean;
  /** Linked celebration for skip / checkout context (sent every turn). */
  contextCelebrationId?: string | null;
  /** MS573: when set, the hook treats the chat session as an active
   *  draft scoped to this (user_id, recipient_id[, celebration_id])
   *  triple. The flag is set to `true` on the session row after the
   *  first successful turn (idempotent — re-firing is a no-op if the
   *  flag is already true). On `reset()`, the flag is cleared back to
   *  `false` for the current session. Hook unmount or scope change to
   *  null does NOT clear the flag — close ≠ abandon; the draft
   *  persists until the user explicitly resets or replaces it.
   *
   *  Partial unique index on (user_id, recipient_id) WHERE
   *  is_active_draft = true enforces one active draft per pair; the
   *  hook defensively clears any OTHER active drafts for the same
   *  (user_id, recipient_id) before flipping the current row to true.
   *
   *  Only the dashboard concierge drawer sets this today (MS573). The
   *  /chat page, landing hero, and FloatingConcierge leave it unset
   *  so their flag-tracking lifecycle is byte-identical to pre-MS573. */
  activeDraftScope?: {
    user_id: string;
    recipient_id: string;
    celebration_id?: string;
  } | null;
  /** MS573: when set (instead of `restoreFromSessionId`), the hook
   *  resolves the target session by querying
   *  `chat_sessions WHERE user_id = X AND recipient_id = Y AND
   *  is_active_draft = true LIMIT 1` and restores from that row. If
   *  no active draft exists for the scope, the hook falls through to
   *  a fresh empty session (no restore happens). The drawer uses this
   *  to reopen a prior conversation for the same recipient without
   *  having to track session ids in component state.
   *
   *  Mutually exclusive with `restoreFromSessionId` in practice — if
   *  both are set, `restoreFromSessionId` wins (explicit id beats
   *  scope lookup). The drawer sets only this. /chat sets only
   *  `restoreFromSessionId`. */
  loadActiveDraftFor?: {
    user_id: string;
    recipient_id: string;
  } | null;
  /** Phase 2g — forwarded to chat-concierge; enables dashboard UI tools. */
  chatSurface?: "dashboard" | "chat" | "landing" | "floating" | string;
  /** Phase 2g — executed when the server returns clientActions. */
  onDashboardClientActions?: DashboardClientActionHandlers | null;
  /** Team page — highlight / open profile from thread widgets. */
  onTeamClientActions?: TeamClientActionHandlers | null;
  /** When set, persist/restore session id under this sessionStorage key
   *  instead of `lembra:hero_session_id` (landing hero / /chat handoff). */
  sessionPersistKey?: string;
}

type RecommendationPresentation = {
  sku_id: string;
  why_this_one: string;
  highlight_fields: { label: string; value: string }[];
};

function toRecommendationPresentation(value: unknown): RecommendationPresentation[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter(
    (item): item is RecommendationPresentation =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as { sku_id?: unknown }).sku_id === "string",
  );
}

/** MS570: shape of the brief restored from `chat_sessions.initial_brief`.
 *  Only the fields the /chat page needs to rehydrate the header are
 *  surfaced; downstream consumers can widen this later if a new
 *  surface needs more of the brief shape. `null` when no restore
 *  was attempted (or when the restore returned no session row);
 *  otherwise a (possibly-empty) object. */
export interface RestoredInitialBrief {
  recipient_name?: string;
  occasion?: string;
}

interface UseInlineConversationReturn {
  sessionId: string | null;
  turns: ChatTurn[];
  chips: string[];
  status: ChatStatus;
  errorMessage: string | null;
  costSnapshot: CostSnapshot | null;
  /** MS598: the assistant turn currently streaming in, or null when no
   *  stream is in flight. Populated ONLY on the flag-gated streaming path
   *  (CHAT_STREAMING_ENABLED, default OFF): as `delta` events arrive its
   *  `text` grows with the accumulated prose, and the resolved carousel
   *  attaches on the `cards` event. Surfaces render it as an in-progress
   *  ChatBubble in the same slot the ThinkingBubble pre-loader occupied,
   *  handing off the moment `status` flips "thinking" → "streaming".
   *
   *  It is a TRANSIENT render-only turn — it is NEVER added to `turns`.
   *  On the terminal `done`/`error` it's cleared to null and the SAME
   *  finalized ChatTurn the buffered path produces is appended to `turns`
   *  (so persistence-parity + the DB-restore render path see an identical
   *  turn — the MS599 seam). With the flag OFF this stays null forever and
   *  the field is inert, keeping every surface byte-identical to pre-MS598. */
  streamingTurn: ChatTurn | null;
  /** MS294: true while the MS288 session-restore effect is in flight.
   *  Surfaces (FloatingConcierge / InlineConcierge empty state) use
   *  this to suppress starter-chip flash during the brief async DB
   *  fetch that hydrates a prior conversation. False on mount once
   *  the restore decision is made (no stored id, or after the load
   *  resolves either way). MS570: also used by Chat.tsx to gate its
   *  canvas-header + chips (replaces the previous `status === "loading"`
   *  gate; equivalent because both flip to false after the same
   *  session-restore fetch resolves). */
  isRestoring: boolean;
  /** MS570: the `initial_brief` JSON column from the restored
   *  chat_sessions row, surfaced when the caller passed
   *  `includeInitialBriefRestore: true`. Null until the restore
   *  resolves (or always null when the option is off). Pages that
   *  show recipient/occasion context in a header use this to
   *  rehydrate that context without owning their own chat_sessions
   *  fetch. URL params still win where applicable — the calling
   *  page enforces that precedence. */
  restoredInitialBrief: RestoredInitialBrief | null;
  /** Latest resolved_brief from chat-concierge (submit_brief / tool loop). */
  latestResolvedBrief: unknown;
  /** MS614: human-readable bullets from session working_brief. */
  workingBriefSummary: string[];
  /** Submit a message (user turn → assistant turn round-trip). */
  submit: (message: string) => Promise<void>;
  /** Submit but tag the capture event as a chip tap instead of
   *  a typed message. Same network call. */
  submitChip: (chipText: string) => Promise<void>;
  /** Execute a structured widget action (confirm / undo / skip). */
  submitWidgetAction: (action: WidgetActionRequest, label?: string) => Promise<void>;
  /** MS204: re-fire the last user turn. Use from the error-state
   *  "Try again" button so the buyer doesn't have to retype.
   *  Returns false if there's nothing to retry (no user turns yet);
   *  callers can hide their retry button on false. */
  retry: () => Promise<boolean>;
  /** Clear all state and return to the cold-start UI. */
  reset: () => void;
  /** MS570: force the conversation into the "completed" status from
   *  outside the engine. The /chat page uses this after an order
   *  submission to drop the surface into the visibly-completed state
   *  ("Ready when you are." / "Anything else?" banner). This is a
   *  presentation-level signal, not engine state — the server's
   *  submit_brief tool also reaches "completed" naturally on its own
   *  turn. Without this hook callers couldn't preserve the pre-MS570
   *  /chat post-submit UX. Other surfaces (InlineConcierge /
   *  FloatingConcierge) don't call this; their onSubmitted handler
   *  just emits a capture event. */
  markCompleted: () => void;
  isInFlight: boolean;
  stopGeneration: () => void;
  editLastUserMessage: () => string | null;
  lastUserTurnId: string | null;
  composerPrefillKey: number;
  composerPrefill: string;
}

export function useInlineConversation({
  initialBrief,
  autoSendFirstMessage = true,
  captureSurface = "landing",
  latencySurface,
  restoreFromSessionId = null,
  includeInitialBriefRestore = false,
  activeDraftScope = null,
  loadActiveDraftFor = null,
  chatSurface,
  onDashboardClientActions = null,
  onTeamClientActions = null,
  contextCelebrationId = null,
  sessionPersistKey = "lembra:hero_session_id",
}: UseInlineConversationOptions = {}): UseInlineConversationReturn {
  // MS591/AUDIT-024: resolve the latency-event surface. Explicit
  // `latencySurface` wins; otherwise map from captureSurface so the two
  // surfaces that pass a distinct captureSurface today ("chat" via
  // Chat.tsx, "landing" via InlineConcierge) are attributed correctly.
  const resolvedLatencySurface: LatencySurface =
    latencySurface ?? (captureSurface === "chat" ? "chat" : "inline");
  const { session, loading: authLoading } = useAuth();
  const isAnon = !authLoading && !session;
  // MS276: invalidate React Query caches after each chat round-trip
  // so chat-driven mutations (MS265 skip_celebration, MS266
  // update_team_member, MS267 create_celebration) reflect in the
  // Dashboard / Team / Calendar / Orders UIs without requiring a
  // page refresh. Cheap by design — see the invalidate call below
  // for the rationale and scope.
  const queryClient = useQueryClient();
  // Guest session id is stable per browser; the edge function uses
  // it to scope the chat_sessions row for anon callers. Authed
  // callers ignore it (the edge function reads the JWT instead).
  const guestSessionId = useMemo(
    () => (isAnon ? getOrCreateGuestSessionId() : null),
    [isAnon],
  );

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [chips, setChips] = useState<string[]>([]);
  const [status, setStatus] = useState<ChatStatus>("ready");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [costSnapshot, setCostSnapshot] = useState<CostSnapshot | null>(null);
  // MS598: the in-progress streaming assistant turn (see the return-type
  // doc). Null unless a flag-gated stream is actively flowing. A fresh,
  // stable id is minted once per turn (on the first delta) so the surface's
  // ChatBubble keyed render doesn't remount mid-stream. Cleared to null at
  // the terminal done/error, immediately before the shared buffered
  // side-effect block appends the finalized turn to `turns`.
  const [streamingTurn, setStreamingTurn] = useState<ChatTurn | null>(null);
  // MS294: gate the empty-state starter chips during MS288 session
  // restore so they don't briefly flash before turns hydrate.
  // Initial true assumes restore COULD fire; the restore effect
  // flips it to false on mount once the decision is made (no
  // stored id → false immediately; storedId present → false after
  // DB fetch resolves). The empty-state UI gates on isRestoring
  // being false AND turns.length === 0.
  const [isRestoring, setIsRestoring] = useState<boolean>(true);
  // MS570: chat_sessions.initial_brief surfaced when
  // includeInitialBriefRestore is true. Lets the /chat page rehydrate
  // its recipient/occasion header without owning its own chat_sessions
  // query. Null until the restore resolves (or always null when the
  // option is off / when there's nothing to restore).
  const [restoredInitialBrief, setRestoredInitialBrief] =
    useState<RestoredInitialBrief | null>(null);
  const [latestResolvedBrief, setLatestResolvedBrief] = useState<unknown>(null);
  const [workingBriefSummary, setWorkingBriefSummary] = useState<string[]>([]);

  // MS201: defer submits fired during the auth-loading window.
  //
  // The race: AuthProvider starts with loading=true and resolves on
  // the first `onAuthStateChange` event (usually <50ms but can take
  // a few hundred ms on a cold visit). During that window:
  //   - isAnon = !true && !session = false
  //   - guestSessionId = null (gated by isAnon)
  //   - body.guestSessionId = isAnon ? guestSessionId : undefined = undefined
  //   - body has no JWT either (session is null in the supabase client)
  //   - edge function sees an unidentified caller → 401 / structured
  //     error response → user sees "Hmm, we had trouble there. Try
  //     again?" on their very first submit, then retry succeeds
  //     because auth has resolved by then.
  //
  // Fix: track a pending submit in a ref. If submit fires during
  // authLoading, store the args + return immediately (no network
  // call yet). An effect watches authLoading; when it flips to
  // false, fires the stored submit. The "thinking" indicator is
  // visible the whole time so the user sees uninterrupted progress.
  //
  // This is separate from MS198's first-turn cold-start retry —
  // that handles NETWORK failures (no `data` returned), this handles
  // the SERVER 401 caused by missing credentials. Both can occur;
  // both are now handled.
  const pendingSubmitRef = useRef<{
    message: string;
    isAutoFromBrief: boolean;
    isChip: boolean;
  } | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const userStoppedRef = useRef(false);
  const [composerPrefillKey, setComposerPrefillKey] = useState(0);
  const [composerPrefill, setComposerPrefill] = useState("");

  const isInFlight = status === "thinking" || status === "streaming";

  const lastUserTurnId = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i]?.role === "user") return turns[i].id;
    }
    return null;
  }, [turns]);

  const invalidateInFlightTurn = useCallback(() => {
    activeTurnIdRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const stopGeneration = useCallback(() => {
    if (status !== "thinking" && status !== "streaming") return;
    userStoppedRef.current = true;
    invalidateInFlightTurn();
    pendingSubmitRef.current = null;
    setStreamingTurn(null);
    setStatus("ready");
    setErrorMessage(null);
    void captureInteraction({
      surface: captureSurface,
      event_type: "generation_stopped",
      payload: { session_id: sessionIdRef.current },
      recipient_id: initialBrief?.recipient_id ?? null,
    });
  }, [status, captureSurface, initialBrief, invalidateInFlightTurn]);

  const editLastUserMessage = useCallback((): string | null => {
    if (isRestoring) return null;
    let lastUserIdx = -1;
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i]?.role === "user") {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx === -1) return null;
    const text = turns[lastUserIdx].text;
    userStoppedRef.current = true;
    invalidateInFlightTurn();
    pendingSubmitRef.current = null;
    setTurns((prev) => prev.slice(0, lastUserIdx));
    setStreamingTurn(null);
    setChips([]);
    setStatus("ready");
    setErrorMessage(null);
    setComposerPrefill(text);
    setComposerPrefillKey((k) => k + 1);
    void captureInteraction({
      surface: captureSurface,
      event_type: "message_edit_started",
      payload: { session_id: sessionIdRef.current, message_len: text.length },
      recipient_id: initialBrief?.recipient_id ?? null,
    });
    return text;
  }, [turns, isRestoring, captureSurface, initialBrief, invalidateInFlightTurn]);

  // Keep latest sessionId addressable inside submit without
  // invalidating the callback on every turn — submit is recreated
  // only when initialBrief or auth state changes.
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
    // MS192: mirror the active sessionId into sessionStorage so
    // landing-page CTAs ("Send a gift" → /chat) can pick it up and
    // forward the user into /chat?sessionId=<id> instead of resetting
    // to an empty conversation. Sibling components / pages don't share
    // React state with this hook; sessionStorage is the cheapest
    // cross-component handoff that survives a navigation.
    //
    // Per-tab scope (sessionStorage, not localStorage) — restoring on
    // a different tab would be confusing ("why is my conversation
    // half-finished?"). Cleared on hard reset below.
    if (typeof window === "undefined") return;
    try {
      if (sessionId) {
        window.sessionStorage.setItem(sessionPersistKey, sessionId);
      } else {
        window.sessionStorage.removeItem(sessionPersistKey);
      }
      // MS192: fire a same-tab custom event so React subscribers
      // (useHeroSessionId in src/lib/heroSession.ts) re-render and
      // pick up the new value. The native `storage` event only
      // fires in OTHER tabs, which doesn't help here.
      window.dispatchEvent(new CustomEvent("lembra:hero_session_changed"));
    } catch {
      // Storage unavailable (private mode / quota) — degrade silently.
      // Worst case the user lands on /chat with a fresh conversation,
      // which is the pre-MS192 behaviour.
    }
  }, [sessionId, sessionPersistKey]);

  // MS597: keep the latest auth access token addressable inside submit
  // WITHOUT adding `session` to submit's dependency array — same ref
  // pattern as sessionIdRef above. This matters for the sibling-surface
  // guarantee: submit's identity (and thus its churn behaviour, which
  // the MS201 auto-replay effect depends on) stays byte-identical to
  // pre-MS597. The token is only read on the MS597 streaming branch
  // (flag-gated, default OFF); the buffered invoke path injects auth via
  // the shared supabase client and never reads this ref.
  const accessTokenRef = useRef<string | null>(null);
  useEffect(() => {
    accessTokenRef.current = session?.access_token ?? null;
  }, [session?.access_token]);

  const submit = useCallback(
    async (message: string, isAutoFromBrief = false, isChip = false) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      // Guard against double-fire from rapid Enter spam — the page
      // state already shows "thinking", but the callback identity
      // hasn't refreshed yet. MS598: "streaming" is mid-turn too (a
      // delta has landed on the flag-gated path), so guard it the same
      // way; inert when the flag is OFF (status never becomes "streaming").
      if (status === "thinking" || status === "streaming") return;

      userStoppedRef.current = false;
      const turnId = crypto.randomUUID();
      activeTurnIdRef.current = turnId;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const turnStillActive = () => activeTurnIdRef.current === turnId;

      // MS201: defer if auth is still resolving. Stash the args, show
      // the thinking state, return early. A useEffect below fires the
      // pending submit once authLoading flips to false. To the user
      // this looks identical to a normal submit, just with a tiny
      // (typically invisible) wait before the network call.
      // MS201: was the submit deferred (auth still loading on the
      // previous call)? If so, the optimistic UI was already pushed —
      // we're now being re-fired by the auth-resolved effect and
      // should skip straight to the network call.
      const isDeferredReplay =
        pendingSubmitRef.current !== null &&
        pendingSubmitRef.current.message === trimmed;
      if (isDeferredReplay) {
        pendingSubmitRef.current = null;
      } else if (authLoading) {
        // First-pass defer: push optimistic, store pending, return.
        // The useEffect below replays once authLoading flips false.
        pendingSubmitRef.current = { message: trimmed, isAutoFromBrief, isChip };
        const optimisticUserTurn: ChatTurn = {
          id: crypto.randomUUID(),
          role: "user",
          text: trimmed,
        };
        setTurns((prev) => [...prev, optimisticUserTurn]);
        setChips([]);
        setStatus("thinking");
        setErrorMessage(null);
        return;
      }

      // Optimistically append the user turn so the UI updates before
      // the network round-trip. The edge function will persist the
      // canonical user message via chat_messages on its side.
      //
      // MS201: skip when we're replaying a deferred submit (the
      // optimistic turn was already pushed during the defer step).
      if (!isDeferredReplay) {
        const userTurn: ChatTurn = {
          id: crypto.randomUUID(),
          role: "user",
          text: trimmed,
        };
        setTurns((prev) => [...prev, userTurn]);
        setChips([]);
        setStatus("thinking");
        setErrorMessage(null);
      }

      void captureInteraction({
        surface: captureSurface,
        event_type: isAutoFromBrief
          ? "session_opened"
          : isChip
            ? "chip_tapped"
            : "message_sent",
        payload: {
          session_id: sessionIdRef.current,
          length: trimmed.length,
          came_from: isAutoFromBrief
            ? "smart_input"
            : isChip
              ? "chip"
              : "composer",
        },
        recipient_id: initialBrief?.recipient_id ?? null,
      });

      try {
        // MS198: single silent retry on the FIRST turn only, to
        // absorb chat-concierge edge-function cold-starts. Lucky
        // flagged on mobile: "first time around it returns an error,
        // then if you persist and try to send the same prompt again,
        // it will successfully go through with it." Classic cold-
        // start pattern — Supabase puts edge functions to sleep
        // after ~5 min of inactivity; the first request after that
        // wakes them (~1-3s Deno JIT + module load + Anthropic
        // round-trip) and can exceed the user's mobile-network
        // patience window.
        //
        // We only retry on the FIRST turn (no sessionIdRef.current
        // yet) because subsequent turns hit a warm function. And we
        // only retry on network-shaped errors — server-returned
        // errors (4xx/5xx with structured body) get surfaced
        // unchanged. The retry is silent (no second "thinking"
        // flash) and capped at one attempt so we don't loop on a
        // genuinely-down function.
        const isFirstTurn = !sessionIdRef.current;
        const body = {
          sessionId: sessionIdRef.current ?? undefined,
          // Pass initialBrief only on the very first turn (when
          // no sessionId exists yet). The edge function uses it
          // to seed chat_sessions.initial_brief and the system
          // prompt with the full context the user typed.
          initialBrief: !sessionIdRef.current ? initialBrief : undefined,
          recipientId: initialBrief?.recipient_id ?? null,
          contextCelebrationId:
            contextCelebrationId ??
            initialBrief?.celebration_id ??
            undefined,
          message: trimmed,
          guestSessionId: isAnon ? guestSessionId : undefined,
          ...(chatSurface ? { chatSurface } : {}),
        };

        // MS591/AUDIT-024: bracket the WHOLE invoke round-trip — this is
        // the client-perceived latency the user actually waits through
        // (network out + full server turn + network back). It spans the
        // MS198 cold-start retry pause + second invoke too, because the
        // user perceived that entire wait as one "thinking" spell. We
        // record whether the cold-start path fired as `cold_start` on the
        // event. Emitted once, after the round-trip resolves, regardless
        // of success/circuit/error outcome (all are "turn completed" from
        // a latency standpoint). The number that matters for UX, and the
        // counterpart to the server-persisted chat_messages.duration_ms.
        const tStart =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        let coldStartRetried = false;

        // invokeResult mirrors the supabase.functions.invoke return shape
        // ({ data, error }) so the downstream side-effect block (session
        // adoption, latency, circuit-breaker/error handling, assistant
        // turn push, chips/cost, captures, status, MS573 flag, MS276
        // invalidations) runs IDENTICALLY whether the turn came from the
        // buffered invoke or the MS597 stream. `data` is the buffered
        // blob shape either way.
        let invokeResult: {
          data: BufferedTurnData | null;
          error: { message: string } | null;
        } | null = null;

        // MS597: flag-gated streaming branch (DARK — flag default OFF, so
        // this whole block is skipped in production and the buffered path
        // below runs byte-identically to pre-MS597). When ON, consume the
        // chat-concierge SSE producer and fold it into the SAME `data`
        // blob. A streaming ESTABLISHMENT failure (network / non-2xx)
        // throws ChatStreamTransportError → we fall through to the
        // buffered invoke path so a stream problem can never make chat
        // worse than today (and the MS198 first-turn cold-start retry
        // still applies to that buffered fallback). A server-sent stream
        // `error` is NOT a transport failure — it comes back as
        // { ok:false, error } and is surfaced like any buffered error.
        if (CHAT_STREAMING_ENABLED) {
          // MS598: mint a stable id for the in-progress streaming turn so
          // the surface's keyed ChatBubble render doesn't remount as the
          // text grows. One id per turn; the finalized turn (appended at
          // done) gets its OWN fresh id from the buffered block below, so
          // the transient streaming bubble and the persisted turn never
          // collide on key.
          const streamingTurnId = crypto.randomUUID();
          try {
            const streamData = await consumeChatStream(
              {
                sessionId: sessionIdRef.current ?? undefined,
                initialBrief: (!sessionIdRef.current ? initialBrief : undefined) as
                  | Record<string, unknown>
                  | undefined,
                recipientId: initialBrief?.recipient_id ?? null,
                message: trimmed,
                guestSessionId: isAnon ? guestSessionId ?? undefined : undefined,
                accessToken: accessTokenRef.current,
                signal: abortController.signal,
                ...(chatSurface ? { chatSurface } : {}),
              },
              {
                // MS597/MS598 seam: adopt a newly-created session id as
                // soon as the `meta` frame lands (mirrors the buffered
                // setSessionId, just earlier in the turn).
                onMeta: (meta) => {
                  if (meta.sessionId && !sessionIdRef.current) {
                    setSessionId(meta.sessionId);
                  }
                },
                // MS598: incremental prose render. On the FIRST delta hand
                // off from the pre-loader (status "thinking" → "streaming")
                // and mount the growing bubble; every delta thereafter
                // grows its text to the running accumulation. The bubble is
                // NOT a `turns` entry — it's the transient `streamingTurn`,
                // rendered in the same slot the ThinkingBubble occupied.
                onDelta: (_chunk, accumulated) => {
                  setStatus((prev) => (prev === "thinking" ? "streaming" : prev));
                  setStreamingTurn({
                    id: streamingTurnId,
                    role: "assistant",
                    text: accumulated,
                    // animate:false — the text is ALREADY arriving
                    // incrementally from the server; the ChatBubble
                    // typewriter would double-animate and lag the real
                    // stream. The growth IS the animation here.
                    animate: false,
                  });
                },
                // MS598: card resolution. Cards arrive atomically AFTER the
                // prose deltas on recommendation turns; attach them to the
                // in-progress bubble so the carousel reveals in place. Cast
                // mirrors the buffered path (data.cards as SkuCardData[]) —
                // the reader forwards the card objects verbatim.
                onCards: (resolvedCards) => {
                  const cards =
                    Array.isArray(resolvedCards) && resolvedCards.length > 0
                      ? (resolvedCards as unknown as SkuCardData[])
                      : undefined;
                  setStreamingTurn((prev) =>
                    prev
                      ? { ...prev, cards }
                      : {
                          // Defensive: a `cards` event with no prior delta
                          // (tool-only turn). Still surface the bubble so
                          // the carousel can resolve in; status hands off
                          // the same way.
                          id: streamingTurnId,
                          role: "assistant",
                          text: "",
                          cards,
                          animate: false,
                        },
                  );
                  setStatus((prev) => (prev === "thinking" ? "streaming" : prev));
                },
              },
            );
            invokeResult = { data: streamData, error: null };
          } catch (streamErr) {
            if (userStoppedRef.current || !turnStillActive()) {
              userStoppedRef.current = false;
              return;
            }
            if (!(streamErr instanceof ChatStreamTransportError)) throw streamErr;
            setStreamingTurn(null);
            setStatus((prev) => (prev === "streaming" ? "thinking" : prev));
            invokeResult = null;
          }
        }

        if (!invokeResult) {
          invokeResult = await supabase.functions.invoke("chat-concierge", { body });
        }

        if (!turnStillActive() || userStoppedRef.current) {
          userStoppedRef.current = false;
          return;
        }

        if (isFirstTurn && invokeResult.error && !invokeResult.data) {
          // First-turn network/cold-start failure — retry once after a
          // short pause so the now-warm function has a clean shot.
          // 800ms = a typical Deno cold-start window; longer than that
          // and the user has noticed the lag anyway.
          coldStartRetried = true;
          await new Promise((r) => setTimeout(r, 800));
          invokeResult = await supabase.functions.invoke("chat-concierge", { body });
          if (!invokeResult.error && invokeResult.data) {
            void captureInteraction({
              surface: captureSurface,
              event_type: "first_turn_cold_start_recovered",
              payload: { session_id: sessionIdRef.current },
              recipient_id: initialBrief?.recipient_id ?? null,
            });
          }
        }

        // MS591: emit client-perceived latency now that the round-trip
        // (incl. any cold-start retry) has resolved. track() is
        // try/catch-wrapped internally, so it can never break the turn.
        const tEnd =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        track(EVENTS.CHAT_TURN_LATENCY, {
          client_perceived_ms: Math.round(tEnd - tStart),
          surface: resolvedLatencySurface,
          session_id: sessionIdRef.current ?? invokeResult.data?.sessionId ?? null,
          cold_start: coldStartRetried,
        });

        if (!turnStillActive() || userStoppedRef.current) {
          userStoppedRef.current = false;
          return;
        }

        // MS598: the stream (or buffered fallback) has resolved — the turn
        // is now terminal. Tear down the transient streaming bubble BEFORE
        // the shared side-effect block below runs, so finalization is a
        // clean handoff: the in-progress `streamingTurn` disappears and the
        // SAME finalized ChatTurn the buffered path always produced is
        // appended to `turns` (identical shape → persistence-parity + the
        // DB-restore render path see no divergence; the MS599 seam). A
        // server-sent stream `error` (data.ok === false) also lands here
        // first, so a half-streamed bubble can never linger behind an error
        // banner. No-op when the flag is OFF (streamingTurn was never set).
        setStreamingTurn(null);

        const { data, error } = invokeResult;
        const serverError =
          (data && typeof data === "object" && "error" in data && typeof data.error === "string"
            ? data.error
            : null) ?? null;

        if (error && serverError) {
          setStatus("error");
          setErrorMessage(serverError);
          void captureInteraction({
            surface: captureSurface,
            event_type: "send_failed",
            payload: {
              session_id: sessionIdRef.current,
              error: serverError,
              server_error: true,
            },
            recipient_id: initialBrief?.recipient_id ?? null,
          });
          return;
        }

        if (error) throw new Error(error.message);
        if (!data?.ok) {
          if (data?.status === "circuit_broken") {
            setStatus("circuit_broken");
            setErrorMessage(
              data?.error ?? "We hit a snag mid-thought, let's start over.",
            );
            void captureInteraction({
              surface: captureSurface,
              event_type: "circuit_broken",
              payload: {
                session_id: data?.sessionId ?? sessionIdRef.current,
              },
              recipient_id: initialBrief?.recipient_id ?? null,
            });
            return;
          }
          // MS303: surface the server's specific error message when one
          // is present (e.g. validation messages like "Brief too long",
          // catalogue lookup failures, etc.) instead of throwing into
          // the catch block which would replace it with the generic
          // "Hmm, we had trouble there. Try again?". The server-side
          // errors are crafted to be user-actionable; the generic
          // fallback is only correct for network/transport failures
          // (which still hit the catch via the `throw error.message`
          // branch above).
          setStatus("error");
          setErrorMessage(
            data?.error ?? "Hmm, we had trouble there. Try again?",
          );
          void captureInteraction({
            surface: captureSurface,
            event_type: "send_failed",
            payload: {
              session_id: sessionIdRef.current,
              error: data?.error ?? "no_error_field",
              server_error: true,
            },
            recipient_id: initialBrief?.recipient_id ?? null,
          });
          return;
        }

        if (data.sessionId && !sessionIdRef.current) {
          setSessionId(data.sessionId);
        }

        // MS573: when the hook is operating in "active draft" mode
        // (drawer surface), stamp the chat_sessions row with
        // is_active_draft = true after the first successful turn so
        // closing-then-reopening the drawer for the same recipient
        // restores this conversation. Fire-and-forget — the flag is
        // an optimisation, not a hard requirement; a failure here
        // shouldn't break the chat turn the user just completed.
        //
        // Guards:
        //   - sessionId resolved (either pre-existing on this turn or
        //     just returned by the edge function)
        //   - activeDraftScope is set (i.e. the drawer wired it; other
        //     surfaces opt out and skip the write entirely)
        //   - We're authed (anon doesn't manage active drafts — the
        //     partial unique index requires user_id IS NOT NULL anyway)
        //
        // Conflict handling: the partial unique index allows only one
        // row with (user_id, recipient_id, is_active_draft=true). If a
        // stale row exists (e.g. the user opened the drawer, reset(),
        // closed without that reset's UPDATE flushing, and reopened —
        // or a tab race), the bare UPDATE on this row would 23505. We
        // defensively clear OTHER active drafts in the same scope
        // first, then flip the current row. Both writes go via the
        // user-scoped supabase client; RLS owner_rw covers them.
        const resolvedSessionId = sessionIdRef.current ?? data.sessionId;
        if (
          activeDraftScope &&
          resolvedSessionId &&
          !isAnon
        ) {
          void (async () => {
            try {
              await supabase
                .from("chat_sessions")
                .update({ is_active_draft: false })
                .eq("user_id", activeDraftScope.user_id)
                .eq("recipient_id", activeDraftScope.recipient_id)
                .eq("is_active_draft", true)
                .neq("id", resolvedSessionId);
              await supabase
                .from("chat_sessions")
                .update({ is_active_draft: true })
                .eq("id", resolvedSessionId);
            } catch {
              // Silent: a failed flag write doesn't degrade the chat
              // turn (the conversation already landed in chat_messages
              // server-side). Worst case: the buyer reopens the drawer
              // and gets a fresh session instead of restoring this one.
            }
          })();
        }

        const replyText = (data.reply ?? "").trim();
        const replyCards =
          Array.isArray(data.cards) && data.cards.length > 0
            ? (data.cards as unknown as SkuCardData[])
            : undefined;
        const replyWidgets = parseThreadWidgets(data.threadWidgets);
        const assistantTurn: ChatTurn = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: data.reply ?? "",
          cards: replyCards,
          threadWidgets: replyWidgets.length > 0 ? replyWidgets : undefined,
          animate: true,
        };
        // MS89: same guard as MS88 on /chat — chat-concierge omits
        // empty assistant rows from the DB but still returns them in
        // the response shape, so tool-only turns (e.g. submit_brief
        // without narrative text) used to grow a ghost bubble in the
        // dashboard InlineConcierge thread. Only push when there's
        // actual content to render.
        if (replyText || replyCards || replyWidgets.length > 0) {
          setTurns((prev) => [...prev, assistantTurn]);
        }
        setChips(Array.isArray(data.chips) ? data.chips : []);
        setCostSnapshot((data.costSnapshot as CostSnapshot | null) ?? null);
        setWorkingBriefSummary(
          Array.isArray(data.workingBriefSummary) ? data.workingBriefSummary : [],
        );

        if (assistantTurn.cards && assistantTurn.cards.length > 0) {
          void captureInteraction({
            surface: captureSurface,
            event_type: "cards_rendered",
            payload: {
              session_id: data.sessionId,
              sku_ids: assistantTurn.cards.map((c) => c.sku_id),
              count: assistantTurn.cards.length,
            },
            recipient_id: initialBrief?.recipient_id ?? null,
          });
        }

        if (data.status === "completed") {
          setStatus("completed");
          void captureInteraction({
            surface: captureSurface,
            event_type: "brief_resolved",
            payload: {
              session_id: data.sessionId,
              resolved_brief: data.resolvedBrief,
            },
            recipient_id: initialBrief?.recipient_id ?? null,
          });
        } else {
          setStatus("ready");
        }

        if (data.resolvedBrief != null) {
          setLatestResolvedBrief(data.resolvedBrief);
        }

        // MS276: invalidate the query keys that chat mutations could
        // have touched, so the Dashboard / Team / Calendar / Orders /
        // Notifications pages refresh without the user navigating
        // away and back. Authed-only because guest chat can't mutate
        // anything (the mutation tools all return
        // not_available_for_guests).
        //
        // The invalidations are coarse on purpose: we don't know
        // from the response shape which (if any) mutation tool the
        // model called this turn, but invalidating the 4 prefixes
        // listed below is cheap (each refetch is a single SELECT)
        // and only happens once per round-trip — not per render.
        // React Query keeps showing stale data while the refetch
        // runs, so the user sees no flicker. The benefit (chat-
        // driven changes appear instantly across surfaces) is
        // worth the small bandwidth cost.
        if (!isAnon) {
          queryClient.invalidateQueries({ queryKey: ["celebrations"] });
          queryClient.invalidateQueries({ queryKey: ["team_members"] });
          queryClient.invalidateQueries({ queryKey: ["orders"] });
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        }

        if (onDashboardClientActions && data.clientActions) {
          applyDashboardClientActions(
            parseDashboardClientActions(data.clientActions),
            onDashboardClientActions,
          );
        }
        if (onTeamClientActions && replyWidgets.length > 0) {
          for (const w of replyWidgets) {
            if (w.type === "team_member_highlight") {
              onTeamClientActions.highlightTeamMember(w.team_member_id);
            }
          }
        }
      } catch (err) {
        if (userStoppedRef.current || activeTurnIdRef.current !== turnId) {
          userStoppedRef.current = false;
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setStreamingTurn(null);
        setStatus("error");
        setErrorMessage("Hmm, we had trouble there. Try again?");
        // MS303: network/transport-shape failures land here. The
        // server-error path above sets server_error:true; this branch
        // is server_error:false so analytics can distinguish the two.
        void captureInteraction({
          surface: captureSurface,
          event_type: "send_failed",
          payload: {
            session_id: sessionIdRef.current,
            error: msg,
            server_error: false,
          },
          recipient_id: initialBrief?.recipient_id ?? null,
        });
      }
    },
    [
      initialBrief,
      isAnon,
      guestSessionId,
      captureSurface,
      // MS591: closed over by the chat_turn_latency emit. Stable per
      // mount (derived from captureSurface/latencySurface), but
      // lint-required since the callback reads it.
      resolvedLatencySurface,
      status,
      // MS201: include authLoading so the callback identity changes
      // when auth flips. The replay-effect below depends on `submit`
      // having the resolved-auth closure when it fires.
      authLoading,
      // MS276: queryClient is stable across renders (React Query
      // memoises) but lint-required as a dep since the callback
      // closes over it for the post-response invalidation.
      queryClient,
      // MS573: activeDraftScope is closed over by the flag-write
      // block after the round-trip; recreate submit when it changes
      // so a recipient switch (drawer Sarah → Marcus) doesn't keep
      // writing the flag against the old scope.
    activeDraftScope,
    chatSurface,
    contextCelebrationId,
    onDashboardClientActions,
      onTeamClientActions,
    ],
  );

  const submitWidgetAction = useCallback(
    async (action: WidgetActionRequest, label?: string) => {
      const trimmed = (label ?? "").trim();
      setStatus("thinking");
      setErrorMessage(null);
      const body = {
        sessionId: sessionIdRef.current ?? undefined,
        message: trimmed || "",
        widgetAction: action,
        contextCelebrationId:
          contextCelebrationId ??
          initialBrief?.celebration_id ??
          undefined,
        guestSessionId: isAnon ? guestSessionId : undefined,
        ...(chatSurface ? { chatSurface } : {}),
      };
      try {
        const { data, error } = await supabase.functions.invoke("chat-concierge", { body });
        if (error || !data?.ok) {
          setStatus("error");
          setErrorMessage(data?.error ?? "Hmm, we had trouble there. Try again?");
          return;
        }
        if (data.sessionId && !sessionIdRef.current) setSessionId(data.sessionId);
        if (trimmed) {
          setTurns((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: "user", text: trimmed },
          ]);
        }
        const replyWidgets = parseThreadWidgets(data.threadWidgets);
        const assistantTurn: ChatTurn = {
          id: crypto.randomUUID(),
          role: "assistant",
          text: data.reply ?? "",
          threadWidgets: replyWidgets.length > 0 ? replyWidgets : undefined,
          animate: true,
        };
        if (assistantTurn.text || replyWidgets.length > 0) {
          setTurns((prev) => [...prev, assistantTurn]);
        }
        setStatus("ready");
        if (!isAnon) {
          queryClient.invalidateQueries({ queryKey: ["celebrations"] });
          queryClient.invalidateQueries({ queryKey: ["team_members"] });
          queryClient.invalidateQueries({ queryKey: ["orders"] });
          queryClient.invalidateQueries({ queryKey: ["budget-period-current"] });
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
          window.dispatchEvent(new Event("lembra:notifications-read"));
        }
        if (onDashboardClientActions && data.clientActions) {
          applyDashboardClientActions(
            parseDashboardClientActions(data.clientActions),
            onDashboardClientActions,
          );
        }
        if (onTeamClientActions && replyWidgets.length > 0) {
          for (const w of replyWidgets) {
            if (w.type === "team_member_highlight") {
              onTeamClientActions.highlightTeamMember(w.team_member_id);
            }
          }
        }
      } catch {
        setStatus("error");
        setErrorMessage("Hmm, we had trouble there. Try again?");
      }
    },
    [isAnon, guestSessionId, chatSurface, contextCelebrationId, initialBrief?.celebration_id, queryClient, onDashboardClientActions, onTeamClientActions],
  );

  // MS201: auto-replay pending submit when auth finishes loading.
  // Fires at most once per pending submit (the replay path inside
  // submit() clears pendingSubmitRef.current).
  useEffect(() => {
    if (!authLoading && pendingSubmitRef.current) {
      const { message, isAutoFromBrief, isChip } = pendingSubmitRef.current;
      void submit(message, isAutoFromBrief, isChip);
    }
  }, [authLoading, submit]);

  // Auto-send the initial brief as the first user message on mount.
  // Guarded by a ref so React StrictMode's double-mount doesn't fire
  // two duplicate first messages in dev (the productionsupabase
  // edge function would happily honour both, but the user would see
  // duplicate turns in the thread).
  //
  // MS570: also short-circuit when `restoreFromSessionId` is set —
  // restoring a prior conversation should never re-fire the original
  // brief. The /chat page sets `autoSendFirstMessage: false` itself
  // in that case, but the belt-and-braces check inside the hook
  // means a caller can pass both safely (URL-restore wins).
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!autoSendFirstMessage) return;
    if (restoreFromSessionId) return;
    // MS573: when loadActiveDraftFor is set, the restore effect below
    // will resolve whether there's an existing draft to restore. Block
    // auto-send during that resolution so we don't race a fresh
    // "first turn" against an existing draft restore. In practice the
    // drawer's brief carries raw_text:"" so this branch never fires —
    // but belt-and-braces in case a future scope-based caller passes
    // a non-empty raw_text.
    if (loadActiveDraftFor) return;
    if (autoSentRef.current) return;
    if (!initialBrief?.raw_text) return;
    autoSentRef.current = true;
    void submit(initialBrief.raw_text, /* isAutoFromBrief */ true);
    // Intentionally not re-running on submit changes — autoSentRef
    // guards against re-fire, and submit's identity churn during
    // status transitions would otherwise loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendFirstMessage, initialBrief?.raw_text, restoreFromSessionId, loadActiveDraftFor]);

  // MS288: restore the most recent chat session from sessionStorage on
  // mount. This fires when the user navigates back to a surface that
  // hosts InlineConcierge (e.g. FloatingConcierge re-mounts after a
  // detour through /browse). Without this, the widget always reset to
  // an empty thread — Lucky's "if I decide to go back to the
  // conversation, it no longer holds the same conversation" report.
  //
  // MS570: extended to support URL-driven restore via the
  // `restoreFromSessionId` option. When set, the /chat page passes
  // the `?sessionId=` URL param here and the hook restores that
  // specific session instead of consulting sessionStorage. This is
  // how Chat.tsx's old "bootstrap" effect now lives inside the hook.
  // URL-driven id wins over sessionStorage (explicit > implicit).
  // When `includeInitialBriefRestore: true` is also set, the restore
  // additionally fetches chat_sessions.initial_brief and exposes it
  // via restoredInitialBrief so the caller can rehydrate header
  // context (Chat.tsx's recipient + occasion).
  //
  // Gates:
  //  - The caller has no raw_text brief OR is using URL-driven
  //    restore (URL-restore overrides the cold-brief path; otherwise
  //    a landing-hero raw_text would conflict with restoring a
  //    persisted-prior-session).
  //  - There's no current sessionId already (don't fight the model
  //    mid-turn).
  //  - A target session id resolves from either URL or sessionStorage
  //    (mirror lives at `lembra:hero_session_id`, populated by the
  //    sessionIdRef effect above).
  //
  // Restore reuses the chat_messages → ChatTurn[] reconstruction from
  // the original Chat.tsx bootstrap effect (the chat-concierge edge
  // function persists messages identically across surfaces).
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    // MS570: URL-driven restore always wins; the sessionStorage path
    // is the fallback. MS573 adds a third path: loadActiveDraftFor
    // (scope-based active-draft lookup) sits between the two —
    // explicit `restoreFromSessionId` beats it (the URL is the user's
    // direct intent), but it beats the sessionStorage fallback (a
    // recipient-scoped lookup is more specific than the cross-cutting
    // hero mirror). When none of the three resolve, the empty-state
    // chips need to render — so we flip isRestoring off in every
    // "no restore" branch below.
    if (sessionId) {
      setIsRestoring(false);
      return;
    }
    // MS294: flip isRestoring off as soon as we know we WON'T restore.
    // Otherwise the empty-state starter chips would sit hidden forever
    // on the landing hero (raw_text path) and other non-restore cases.
    // MS570: when `restoreFromSessionId` is set, the URL signals an
    // explicit restore intent that beats any raw_text the SmartIntentBar
    // / hero handoff might have left in initialBrief.
    // MS573: same exception for loadActiveDraftFor — the drawer opens
    // an empty composer, so raw_text is "" by construction and never
    // hits this branch, but belt-and-braces in case a future caller
    // passes both.
    if (!restoreFromSessionId && !loadActiveDraftFor && initialBrief?.raw_text) {
      setIsRestoring(false);
      return;
    }
    if (typeof window === "undefined") {
      setIsRestoring(false);
      return;
    }

    // MS573: when loadActiveDraftFor is set (drawer mode), the restore
    // is a two-step async dance — first resolve the session id by
    // querying chat_sessions for the active draft matching the
    // (user_id, recipient_id) scope, then fall through to the existing
    // messages-restore logic with that id. Move the entire restore
    // into an async IIFE so we can await the scope lookup before
    // committing to a restore path.
    //
    // Guard the ref BEFORE the async block so React StrictMode's
    // double-mount doesn't fire two parallel restores (the second one
    // would race the first's setSessionId and could short-circuit on
    // the `if (sessionId)` guard above only after the state had
    // committed).
    restoredRef.current = true;

    // MS570: track whether we resolved the id from the URL — only the
    // sessionStorage branch should self-clean on stale ids. A URL-passed
    // id is the user's explicit intent; if it's stale we surface an
    // error to the caller instead of silently wiping it.
    // MS573: loadActiveDraftFor behaves like URL-passed (explicit
    // intent: "show me this scope's active draft") — if the resolved
    // id turns up empty, surface an error rather than silently wiping.
    const cameFromExplicitIntent =
      (restoreFromSessionId !== null && restoreFromSessionId !== undefined) ||
      !!loadActiveDraftFor;
    const cameFromUrl = cameFromExplicitIntent;
    (async () => {
      let storedId: string | null = restoreFromSessionId ?? null;

      // MS573: scope-based resolution — query chat_sessions for the
      // active draft matching the (user_id, recipient_id) scope. Only
      // fires when restoreFromSessionId is unset (URL beats scope). If
      // no active draft exists for the scope, falls through to a fresh
      // session (NOT an error — the buyer simply hasn't started a
      // draft for this recipient yet, which is the common case for a
      // first-time drawer open).
      if (!storedId && loadActiveDraftFor) {
        const { data: draftRow, error: draftErr } = await supabase
          .from("chat_sessions")
          .select("id")
          .eq("user_id", loadActiveDraftFor.user_id)
          .eq("recipient_id", loadActiveDraftFor.recipient_id)
          .eq("is_active_draft", true)
          .limit(1)
          .maybeSingle();
        if (draftErr) {
          // RLS denied / network — treat as "no draft" rather than
          // hard-failing the drawer open. The buyer can still start a
          // fresh conversation; if the underlying issue is persistent
          // they'll see other queries fail too.
          setIsRestoring(false);
          return;
        }
        storedId = draftRow?.id ?? null;
        if (!storedId) {
          // Scope resolved cleanly to "no active draft" — the buyer
          // hasn't drafted for this recipient yet. Fresh session is
          // the right starting state. Flip isRestoring off so the
          // empty composer + InlineConcierge starter chips render.
          setIsRestoring(false);
          return;
        }
      }

      // MS570 fallback (preserved): sessionStorage mirror when neither
      // restoreFromSessionId nor loadActiveDraftFor resolved an id.
      if (!storedId) {
        try {
          storedId = window.sessionStorage.getItem(sessionPersistKey);
        } catch {
          setIsRestoring(false);
          return; // sessionStorage unavailable (private mode / quota)
        }
      }
      if (!storedId) {
        setIsRestoring(false);
        return;
      }
      // MS570: when the caller asked for it, pair the messages query
      // with a chat_sessions.initial_brief fetch so the page can
      // rehydrate header context without owning its own query.
      // Promise.all matches Chat.tsx's old bootstrap shape (parallel
      // round-trips) — one DB request adds no measurable latency.
      const [sessionResult, messagesResult] = await Promise.all([
        includeInitialBriefRestore
          ? supabase
              .from("chat_sessions")
              .select("initial_brief")
              .eq("id", storedId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null } as { data: null; error: null }),
        supabase
          .from("chat_messages")
          .select("role, content, presentation, chips, rendered_sku_ids, created_at")
          .eq("session_id", storedId)
          .order("created_at", { ascending: true }),
      ]);
      const { data: messages, error } = messagesResult;
      // MS443 parity: when the caller asked for the initial_brief fetch,
      // a failure on that query is also unrecoverable — without the
      // session row Chat.tsx can't rehydrate its header. Surface an
      // error so the page can tell the user the conversation didn't
      // load, rather than rendering an empty thread silently.
      if (includeInitialBriefRestore && sessionResult.error) {
        setStatus("error");
        setErrorMessage("Couldn't load that conversation.");
        setIsRestoring(false);
        return;
      }
      // Expose the restored brief (may be empty {} if the row exists
      // but the column is null/empty). Setting to null when the column
      // is absent keeps the consumer's "brief unknown" state distinct
      // from "brief was empty".
      if (includeInitialBriefRestore) {
        const brief =
          (sessionResult.data?.initial_brief ?? null) as RestoredInitialBrief | null;
        setRestoredInitialBrief(brief);
      }

      if (error || !messages || messages.length === 0) {
        // sessionStorage path: stale or empty session — clear the
        // mirror so the widget doesn't keep trying to restore a dead
        // conversation. URL path: leave it alone (the user owns the
        // URL); surface an error instead.
        if (!cameFromUrl) {
          try { window.sessionStorage.removeItem(sessionPersistKey); } catch { /* noop */ }
        } else if (error) {
          setStatus("error");
          setErrorMessage("Couldn't load that conversation.");
        }
        // MS294: release the empty-state chips so they render now
        // that we've decided NOT to restore. Without this, the
        // empty thread sits indefinitely with no starter affordance.
        setIsRestoring(false);
        return;
      }

      const restored: ChatTurn[] = [];
      let lastAssistantPresentation: RecommendationPresentation[] | null = null;
      let lastChips: string[] = [];

      for (const m of messages) {
        if (m.role === "user") {
          restored.push({ id: crypto.randomUUID(), role: "user", text: m.content });
        } else if (m.role === "assistant" && m.content?.trim()) {
          restored.push({ id: crypto.randomUUID(), role: "assistant", text: m.content, cards: undefined });
        } else if (m.role === "tool_use") {
          try {
            const block = JSON.parse(m.content);
            if (block?.name === "present_recommendations") {
              lastAssistantPresentation = toRecommendationPresentation(block.input?.cards);
              if (m.presentation) {
                lastAssistantPresentation = toRecommendationPresentation(m.presentation);
              }
            }
          } catch { /* skip non-JSON tool_use */ }
        }
        if (Array.isArray(m.chips)) lastChips = m.chips;
      }

      // Hydrate the last assistant turn's cards, mirroring Chat.tsx's
      // restore logic including the MS285 is_available + deleted_at
      // filters so since-paused SKUs drop out of the restored carousel.
      if (lastAssistantPresentation && restored.length > 0) {
        const lastAssistant = [...restored].reverse().find((t) => t.role === "assistant");
        if (lastAssistant) {
          const ids = lastAssistantPresentation.map((p) => p.sku_id);
          const { data: hydrated } = await supabase
            .from("vendor_products")
            // MS312: select lead_time_days too so hydrated cards can
            // feed OrderConfirmSheet's delivery-date validator.
            // MS318: include dietary_tags for the recipient-conflict
            // warning in OrderConfirmSheet.
            .select("id, name, description, price, image_url, slug, vendor_category, is_voucher, lead_time_days, dietary_tags, vendor_partners(vendor_name)")
            .in("id", ids)
            .eq("is_available", true)
            .is("deleted_at", null);
          const byId = new Map<string, Record<string, unknown>>();
          for (const r of hydrated ?? []) byId.set(r.id as string, r as Record<string, unknown>);
          const cards: SkuCardData[] = [];
          for (const p of lastAssistantPresentation) {
            const row = byId.get(p.sku_id);
            if (!row) continue;
            const vendor = row.vendor_partners as { vendor_name?: string } | null;
            cards.push({
              sku_id: p.sku_id,
              name: String(row.name ?? "Untitled"),
              vendor_name: vendor?.vendor_name ?? "Lembra",
              vendor_category: String(row.vendor_category ?? "other"),
              price_aud: Number(row.price ?? 0),
              image_url: (row.image_url as string | null) ?? null,
              slug: String(row.slug ?? ""),
              description: (row.description as string | null) ?? null,
              is_voucher: Boolean(row.is_voucher),
              lead_time_days: (row.lead_time_days as number | null) ?? null,
              // MS318: dietary_tags propagated for recipient-conflict warning.
              dietary_tags: (row.dietary_tags as string[] | null) ?? null,
              why_this_one: p.why_this_one,
              highlight_fields: p.highlight_fields ?? [],
            });
          }
          lastAssistant.cards = cards;
        }
      }

      setSessionId(storedId);
      setTurns(restored);
      setChips(lastChips);
      setStatus("ready");
      // Block the autoSendFirstMessage path since we've already
      // populated the thread — without this, an empty raw_text would
      // still let the auto-send guard pass (no-op since raw_text is
      // empty) but the restore is what we want.
      autoSentRef.current = true;
      // MS294: restore finished — the empty-state chips gate on
      // turns.length === 0 anyway, so they stay hidden naturally
      // (turns is now populated), but we flip isRestoring off for
      // symmetry with the no-restore branches above.
      setIsRestoring(false);
    })();
    // MS570: restoreFromSessionId + includeInitialBriefRestore are
    // additions to the gates above; without them in deps a re-render
    // that flips them post-mount (unlikely in practice — Chat.tsx
    // derives restoreFromSessionId from a URL param on first paint)
    // would not retry. The restoredRef guard prevents a double-run.
    // MS573: loadActiveDraftFor added for the same reason — the
    // drawer remounts on recipient change (via `key={recipient.id}`),
    // which gives us a fresh hook instance, so the dep isn't strictly
    // load-bearing in practice. But: if a future caller mutated the
    // scope in place (without a remount), we'd want the lookup to
    // re-run. Keep the dep for correctness; the restoredRef guard
    // still prevents double-runs within the same mount.
    //
    // Object identity for loadActiveDraftFor: callers should memoise
    // it; the drawer does (constructs it from recipient + user_id
    // which both come from stable state). An unmemoised scope would
    // re-trigger the effect every render BUT the ref guard means only
    // the first run does work.
  }, [
    initialBrief?.raw_text,
    sessionId,
    sessionPersistKey,
    restoreFromSessionId,
    includeInitialBriefRestore,
    loadActiveDraftFor,
  ]);

  const reset = useCallback(() => {
    void captureInteraction({
      surface: captureSurface,
      event_type: "fresh_chat_started",
      payload: { previous_session_id: sessionIdRef.current },
      recipient_id: initialBrief?.recipient_id ?? null,
    });
    // MS573: when the surface is in active-draft mode and a session
    // exists, clear its is_active_draft flag BEFORE wiping local state.
    // The user explicitly chose to start fresh, so this draft is no
    // longer active. Fire-and-forget — the local reset proceeds even
    // if the write fails (the index has WHERE is_active_draft = TRUE
    // so a stale row only blocks future drafts for the same recipient,
    // not the current reset).
    const sessionToClear = sessionIdRef.current;
    if (activeDraftScope && sessionToClear && !isAnon) {
      void supabase
        .from("chat_sessions")
        .update({ is_active_draft: false })
        .eq("id", sessionToClear)
        .then(() => undefined, () => undefined);
    }
    setSessionId(null);
    setTurns([]);
    setChips([]);
    setStatus("ready");
    setErrorMessage(null);
    setCostSnapshot(null);
    // MS598: drop any in-progress streaming bubble on a hard reset. No-op
    // when the flag is OFF (it's always null then).
    setStreamingTurn(null);
    invalidateInFlightTurn();
    userStoppedRef.current = false;
    setComposerPrefill("");
    setRestoredInitialBrief(null);
    setLatestResolvedBrief(null);
    setWorkingBriefSummary([]);
    restoredRef.current = false;
    autoSentRef.current = false;
  }, [captureSurface, initialBrief, activeDraftScope, isAnon, invalidateInFlightTurn]);

  const submitChip = useCallback(
    (chipText: string) => submit(chipText, false, true),
    [submit],
  );

  // MS570: caller-driven "completed" signal. See markCompleted's
  // doc in UseInlineConversationReturn for rationale.
  const markCompleted = useCallback(() => {
    setStatus("completed");
  }, []);

  // MS204: re-fire the last user turn. Walks `turns` backwards to
  // find the most recent role:"user" entry, pops the trailing
  // failed-attempt artefacts off the thread (the optimistic user
  // turn + any prior error rendering — the error banner is in state,
  // not in turns, so just removing the optimistic turn is enough),
  // and re-submits the text. The submit call will push its own
  // optimistic turn, so removing the prior one keeps the thread
  // visually clean.
  const retry = useCallback(async (): Promise<boolean> => {
    const lastUser = [...turns].reverse().find((t) => t.role === "user");
    if (!lastUser) return false;
    setTurns((prev) => {
      // Drop the trailing user turn (the one we're retrying). If the
      // failure came after a successful assistant turn, the user
      // turn we're popping is the most recent in the array — pop one.
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i]?.role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
    setErrorMessage(null);
    setStatus("ready");
    void captureInteraction({
      surface: captureSurface,
      event_type: "retry_clicked",
      payload: { session_id: sessionIdRef.current, message_len: lastUser.text.length },
      recipient_id: initialBrief?.recipient_id ?? null,
    });
    await submit(lastUser.text, false, false);
    return true;
  }, [turns, submit, captureSurface, initialBrief]);

  return {
    sessionId,
    turns,
    chips,
    status,
    errorMessage,
    costSnapshot,
    streamingTurn,
    isRestoring,
    restoredInitialBrief,
    latestResolvedBrief,
    workingBriefSummary,
    submit: (msg) => submit(msg, false, false),
    submitChip,
    submitWidgetAction,
    retry,
    reset,
    markCompleted,
    isInFlight,
    stopGeneration,
    editLastUserMessage,
    lastUserTurnId,
    composerPrefillKey,
    composerPrefill,
  };
}
